<script lang="ts" context="module">
    export type Choice<T> = {
        value: T;
        key: string;
        label: string;
    };
</script>

<script lang="ts" generics="TValues, TValue">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { cx } from '$lib/utils/cx.js';
    import type { ChangeEvent } from '$lib/utils/types.js';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, readonly TValue[]>;

    export let choices: readonly Choice<TValue>[];

    export let max: number | null = null;

    let className: string = '';
    export { className as class };
    export let defaultClass = 'grid grid-cols-2 gap-x-[16px] gap-y-[0px]';

    export let defaultLabelClass = cx(
        'flex items-center',
        'px-[10px]',
        'border hover:border-brand-blue border-solid focus-within:border-brand-blue border-solid',
        'rounded-[4px]',
        'cursor-pointer',
        'bg-white border-gray-200',
        'data-[checked]:bg-blue-50 data-[checked]:border-brand-blue',
        'data-[disabled]:bg-gray-100/50 data-[disabled]:cursor-not-allowed data-[disabled]:hover:border-gray-200',
    );

    export let labelClass = '';

    export let defaultInputClass = 'size-[16px] shrink-0 cursor-pointer m-0 p-0';
    export let inputClass = '';

    export let defaultTextClass = cx(
        'w-full py-4 ml-2 text-14 text-gray-800 font-normal',
        'data-[checked]:font-bold',
        'data-[disabled]:text-gray-600/80',
    );
    export let textClass = '';
    export let id: string | null = null;

    function onCheckboxInput(event: ChangeEvent<HTMLInputElement>) {
        const key = event.currentTarget.value;
        const choice = choices.find((choice) => choice.key == key);

        if (choice != null) {
            if ($v.includes(choice.value)) {
                event.currentTarget.checked = false;
                form.dispatch(
                    form.actions().changeValue(
                        $v.filter((value) => value !== choice.value),
                        field,
                    ),
                );
            } else {
                if (max === null || $v.length < max) {
                    event.currentTarget.checked = true;
                    form.dispatch(
                        form.actions().changeValue([...$v, choice.value], field),
                    );
                } else {
                    event.currentTarget.checked = false;
                    event.preventDefault();
                }
            }
        }
    }

    function onBlur() {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus() {
        form.dispatch(form.actions().focus(field));
    }

    $: v = form.stores().fieldValue(field);
    $: nbSubmits = form.stores().nbSubmits();
    $: isAlreadyBlur = form.stores().isAlreadyBlur(field);
    $: hasFieldErrors = form.stores().hasFieldErrors(field);
    $: effectiveId = id ?? form.getFieldId(field);
</script>

<div
    class={cx(defaultClass, className)}
    class:is-invalid={($nbSubmits > 0 || $isAlreadyBlur) && $hasFieldErrors}
>
    {#each choices as c (c.key)}
        {@const checked = $v.includes(c.value) ? true : undefined}
        {@const notChecked = !$v.includes(c.value) ? true : undefined}
        {@const isDisabled = notChecked && max != null && $v.length >= max}
        <label
            data-checked={checked}
            data-not-checked={notChecked}
            data-disabled={isDisabled ? true : undefined}
            class={cx(defaultLabelClass, labelClass)}
            for="{effectiveId}.key-{c.key}"
        >
            <input
                data-checked={checked}
                data-not-checked={notChecked}
                data-disabled={isDisabled ? true : undefined}
                class={cx(defaultInputClass, inputClass)}
                type="checkbox"
                id="{effectiveId}.key-{c.key}"
                name={effectiveId}
                value={c.key}
                disabled={isDisabled}
                {checked}
                on:input={onCheckboxInput}
                on:focus={onFocus}
                on:blur={onBlur}
            />
            <span
                data-checked={checked}
                data-not-checked={notChecked}
                data-disabled={isDisabled ? true : undefined}
                class={cx(defaultTextClass, textClass)}
            >
                {c.label}
            </span>
        </label>
    {/each}
</div>
